// hooks/useRegisterDevice.ts
import { useCallback } from 'react';
import { useRegisterDeviceMutation } from 'generated/graphql';
import { getFcmTokenFromSW } from 'serviceWorker';
import { getMessaging } from 'services/firebase';
import { toast } from 'react-toastify';

/* ---------- helpers --------------------------------------------------- */
function base64UrlToUint8(b64: string): Uint8Array {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const b64Std = (b64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(b64Std), (c) => c.charCodeAt(0));
}

/** Compares two Uint8Array in constant-time. */
function equalUint8(a?: BufferSource | null, b?: Uint8Array): boolean {
  if (!a || !b) return false;
  const aa = new Uint8Array(a as ArrayBufferLike);
  if (aa.length !== b.length) return false;
  // `crypto.subtle.timingSafeEqual` is supported on Safari 15+
  // fall back to manual loop if unavailable
  if (crypto.subtle && (crypto.subtle as any).timingSafeEqual) {
    return (crypto.subtle as any).timingSafeEqual(aa, b);
  }
  let diff = 0;
  for (let i = 0; i < aa.length; i++) diff |= aa[i] ^ b[i];
  return diff === 0;
}

/* ---------- hook ------------------------------------------------------ */
export function useRegisterDevice() {
  const [register] = useRegisterDeviceMutation();
  const hasNotification = typeof Notification !== 'undefined';

  // Basic feature-detection (runs synchronously)
  const isSupported =
    'Notification' in window &&
    'serviceWorker' in navigator &&
    window.isSecureContext;

  /* ---------------- handler to call from a user gesture --------------- */
  const enablePush = useCallback(async () => {
    console.log('[push] enablePush() called');
    if (!isSupported) {
      console.warn('[push] Browser not supported for push');
      return;
    }

    /* 1 ▸ Ask permission first (required by Safari/iOS) */
    const permission =
      Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
    console.log('[push] Notification permission:', permission);
    if (permission !== 'granted') return;

    /* 2 ▸ Wait for the active service worker */
    const reg = await navigator.serviceWorker.ready;
    console.log('[push] Service-worker ready, scope:', reg.scope);
    if (!reg.pushManager) {
      toast.error('PushManager not available (iOS tab or flags off)');
      return;
    }

    /* 3 ▸ Chrome / Edge / Firefox via Firebase Cloud Messaging */
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (!isSafari) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const messaging = getMessaging(); // may throw on Safari
        console.log('[push] Firebase Messaging detected');
        const fcmToken = await getFcmTokenFromSW();
        // const fcmToken = await Promise.race([
        //   getFcmTokenFromSW(),
        //   new Promise<null>((_, r) => setTimeout(() => r(null), 2000)),
        // ]);

        if (fcmToken) {
          console.log('[push] FCM token:', fcmToken);
          await register({
            variables: { input: { kind: 'fcm', token: fcmToken } },
          });
          console.log('[push] FCM token sent to backend');
          return; // we’re done for non-Safari browsers
        }
      } catch {
        /* ignore – falls back to Web Push */
      }
    }

    /* 4 ▸ Safari & other browsers via plain Web Push ------------- */
    const PUBLIC_KEY = process.env.REACT_APP_VITE_VAPID_PUBLIC_KEY!;
    const expectedKey = base64UrlToUint8(PUBLIC_KEY);

    let sub = await reg.pushManager.getSubscription();

    // If a subscription exists but with a different VAPID key, discard it
    if (
      sub &&
      sub.options &&
      !equalUint8(sub.options.applicationServerKey, expectedKey)
    ) {
      console.log('[push] Subscription with foreign public key → unsubscribe');
      await sub.unsubscribe();
      sub = null;
    }

    if (!sub) {
      console.log('[push] Creating new subscription…');
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: expectedKey,
      });
      console.log('[push] New subscription created', sub);
    } else {
      console.log('[push] Existing subscription is valid');
    }

    /* 5 ▸ Send the subscription to the backend */
    await register({
      variables: {
        input: { kind: 'webpush', subscription: JSON.stringify(sub) },
      },
    });
    console.log('[push] Web-Push subscription sent to backend');
  }, [isSupported, register]);

  return {
    enablePush,
    isSupported,
    isAlreadyGranted: hasNotification && Notification.permission === 'granted',
  };
}
